<template>
  <div class="kyc-view">
    <van-nav-bar
        title="Identity Verification"
        left-arrow
        fixed
        placeholder
        @click-left="onClickLeft"
        class="custom-navbar"
    />

    <div class="content">
      <!-- Info Header -->
      <div class="info-banner">
        <van-icon name="shield-o" class="banner-icon" />
        <div class="banner-text">
          <h4>Secure Verification</h4>
          <p>Please provide your official documents to verify your identity and unlock all features.</p>
        </div>
      </div>

      <div class="form-section">
        <!-- Document Upload -->
        <div class="form-card">
          <h3 class="card-title">Passport Documents</h3>
          <div class="uploader-grid">
            <div class="upload-item">
              <van-uploader v-model="fileList" :after-read="afterRead" max-count="1">
                <div class="upload-box" :class="{ 'has-file': fileList.length > 0 }">
                  <van-icon :name="fileList.length > 0 ? 'success' : 'photograph'" />
                  <span>Front Side</span>
                </div>
              </van-uploader>
            </div>
            <div class="upload-item">
              <van-uploader v-model="fileList2" :after-read="afterRead" max-count="1">
                <div class="upload-box" :class="{ 'has-file': fileList2.length > 0 }">
                  <van-icon :name="fileList2.length > 0 ? 'success' : 'photograph'" />
                  <span>Back Side</span>
                </div>
              </van-uploader>
            </div>
          </div>
          <p class="upload-hint">Ensure all details are clearly visible and no glare is present.</p>
        </div>

        <!-- Personal Details -->
        <div class="form-card">
          <h3 class="card-title">Personal Details</h3>
          <div class="input-group">
            <label>Full Name</label>
            <div class="field-wrapper">
              <van-field
                  v-model="fullName"
                  placeholder="As shown on passport"
                  :border="false"
              />
            </div>
          </div>
          
          <div class="input-group">
            <label>Passport Number</label>
            <div class="field-wrapper">
              <van-field
                  v-model="passportNumber"
                  placeholder="Document number"
                  :border="false"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="action-footer">
        <van-button
            type="primary"
            block
            round
            class="submit-btn"
            @click="handleSubmit"
        >
          Submit for Review
        </van-button>
        <p class="security-note">
          <van-icon name="lock" /> 
          Your data is encrypted and used only for verification.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { showToast } from "vant";
import { updatePay_orderRealname } from "@/utils/api";

const fileList = ref([]);
const fileList2 = ref([]);
const fullName = ref("");
const passportNumber = ref("");

const onClickLeft = () => history.back();

const afterRead = (file) => {
  console.log("File loaded:", file);
};

const handleSubmit = async () => {
  if (
    !fullName.value ||
    !passportNumber.value ||
    fileList.value.length === 0 ||
    fileList2.value.length === 0
  ) {
    return showToast("Please complete all fields and uploads");
  }

  const formData = new FormData();
  formData.append("realName", fullName.value);
  formData.append("idNo", passportNumber.value);
  formData.append("files", fileList.value[0].file);
  formData.append("files", fileList2.value[0].file);

  try {
    await updatePay_orderRealname(formData);
    showToast("Submitted successfully");
    history.back();
  } catch (error) {
    showToast("Submission failed, please try again");
  }
};
</script>

<style scoped>
.kyc-view {
  background: #f8f9fa;
  min-height: 100vh;
}

:deep(.custom-navbar) {
  background: #fff;
}

.content {
  padding: 20px;
}

.info-banner {
  background: #fff;
  border-radius: 24px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
}

.banner-icon {
  font-size: 32px;
  color: #ff9f00;
  background: #fff9f0;
  padding: 12px;
  border-radius: 16px;
}

.banner-text h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 800;
}

.banner-text p {
  margin: 0;
  font-size: 12px;
  color: #8e8e93;
  line-height: 1.4;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-card {
  background: #fff;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
}

.card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.uploader-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.upload-box {
  width: 100%;
  height: 120px;
  background: #f8f9fb;
  border: 1.5px dashed #d1d1d6;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8e8e93;
  transition: all 0.3s;
}

.upload-box.has-file {
  border-style: solid;
  border-color: #4cd964;
  background: #f0faf2;
  color: #4cd964;
}

.upload-box .van-icon {
  font-size: 28px;
}

.upload-box span {
  font-size: 11px;
  font-weight: 600;
}

.upload-hint {
  font-size: 11px;
  color: #8e8e93;
  line-height: 1.4;
  margin: 0;
}

.input-group {
  margin-bottom: 20px;
}

.input-group:last-child { margin-bottom: 0; }

.input-group label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #8e8e93;
  margin-bottom: 8px;
}

.field-wrapper {
  background: #f8f9fb;
  border-radius: 16px;
  padding: 4px 12px;
  border: 1.5px solid #f0f0f5;
  transition: all 0.3s;
}

.field-wrapper:focus-within {
  border-color: #ff9f00;
  background: #fff;
}

.action-footer {
  margin-top: 40px;
  text-align: center;
}

.submit-btn {
  height: 56px;
  font-weight: 800;
  background: #ff9f00;
  border: none;
  box-shadow: 0 12px 30px rgba(255, 159, 0, 0.25);
}

.security-note {
  margin-top: 16px;
  font-size: 11px;
  color: #c7c7cc;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
</style>
